/* import __COLOCATED_TEMPLATE__ from './add-replacement-item-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import type IntlService from 'ember-intl/services/intl';
import type {
  ShopifyOrder,
  ShopifyLineItem,
} from 'embercom/components/inbox2/conversation-details-sidebar/sidebar-sections/shopify';

interface SelectedLineItem {
  lineItemId: number;
  quantity: number;
}

interface AddReplacementItemModalArgs {
  isOpen: boolean;
  onModalClose: () => void;
  order: ShopifyOrder;
  shopifyDomain: string | null;
  onReplacementSuccess?: () => void;
}

export default class AddReplacementItemModal extends Component<AddReplacementItemModalArgs> {
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: $TSFixMe;
  @service declare intl: IntlService;

  @tracked selectedReasonValue = '';
  @tracked notifyCustomer = false;
  @tracked selectedLineItems: SelectedLineItem[] = [];

  get reason() {
    let selectedReason = this.replacementReasons.find((r) => r.value === this.selectedReasonValue);
    return selectedReason?.label || '';
  }

  get replacementReasons() {
    return [
      {
        value: 'damaged_in_transit',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.add-replacement-modal.reasons.damaged_in_transit',
        ),
      },
      {
        value: 'wrong_item',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.add-replacement-modal.reasons.wrong_item',
        ),
      },
      {
        value: 'defective',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.add-replacement-modal.reasons.defective',
        ),
      },
      {
        value: 'missing_item',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.add-replacement-modal.reasons.missing_item',
        ),
      },
      {
        value: 'poor_quality',
        label: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.add-replacement-modal.reasons.poor_quality',
        ),
      },
    ];
  }

  constructor(owner: unknown, args: AddReplacementItemModalArgs) {
    super(owner, args);
    this.resetModalData();
  }

  get currencySymbol() {
    return this.args.order.total?.charAt(0) ?? '$';
  }

  get hasSelectedItems() {
    return this.selectedLineItems.some((item) => item.quantity > 0);
  }

  get isAddReplacementTaskRunning() {
    return taskFor(this.addReplacementTask).isRunning;
  }

  get isLineItemReplaceable() {
    return (lineItem: ShopifyLineItem): boolean => {
      let alreadyRefunded = lineItem.already_refunded ?? 0;
      let quantityReplaceable = lineItem.quantity_refundable ?? lineItem.quantity - alreadyRefunded;
      return quantityReplaceable > 0;
    };
  }

  @action
  isLineItemSelected(lineItemId: number): boolean {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem ? selectedItem.quantity > 0 : false;
  }

  @action
  getSelectedQuantity(lineItemId: number): number {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem?.quantity || 0;
  }

  @action
  onReasonChange(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.selectedReasonValue = target.value;
  }

  @action
  toggleLineItemSelection(lineItemId: number) {
    let isCurrentlySelected = this.isLineItemSelected(lineItemId);
    let lineItem = this.args.order.line_items.find((item) => item.id === lineItemId);

    if (!lineItem) {
      return;
    }

    let targetQuantity = isCurrentlySelected ? 0 : lineItem.quantity;
    this.updateLineItemQuantityValue(lineItemId, targetQuantity);
  }

  @action
  updateLineItemQuantity(lineItemId: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let quantity = parseInt(target.value, 10);
    this.updateLineItemQuantityValue(lineItemId, quantity);
  }

  private updateLineItemQuantityValue(lineItemId: number, quantity: number) {
    let existingItemIndex = this.selectedLineItems.findIndex(
      (item) => item.lineItemId === lineItemId,
    );

    if (existingItemIndex !== -1) {
      this.selectedLineItems[existingItemIndex] = {
        ...this.selectedLineItems[existingItemIndex],
        quantity: Math.max(0, quantity),
      };
    } else {
      this.selectedLineItems = [
        ...this.selectedLineItems,
        {
          lineItemId,
          quantity: Math.max(0, quantity),
        },
      ];
    }

    this.selectedLineItems = [...this.selectedLineItems];
  }

  @action
  resetModalData() {
    this.selectedReasonValue = '';
    this.notifyCustomer = false;
    this.selectedLineItems = this.args.order.line_items
      .filter((item) => item.id)
      .map((item) => ({
        lineItemId: item.id,
        quantity: 0,
      }));
  }

  @action
  handleModalClose() {
    this.resetModalData();
    this.args.onModalClose();
  }

  @restartableTask
  *addReplacementTask(): TaskGenerator<void> {
    try {
      let itemsToAdd = this.selectedLineItems.filter((item) => item.quantity > 0);

      if (itemsToAdd.length === 0) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.add-replacement-modal.errors.no-items-selected',
          ),
        );
        return;
      }

      if (!this.reason.trim()) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.add-replacement-modal.errors.no-reason',
          ),
        );
        return;
      }

      for (let selectedItem of itemsToAdd) {
        yield ajax({
          url: '/ember/inbox/shopify/add_replacement_item',
          type: 'POST',
          data: JSON.stringify({
            app_id: this.session.workspace.id,
            order_id: this.args.order.id,
            line_item_id: selectedItem.lineItemId,
            quantity: selectedItem.quantity,
            reason: this.reason,
            notify_customer: this.notifyCustomer,
            shopify_domain: this.args.shopifyDomain,
          }),
        });
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('inbox.conversation-details-sidebar.shopify.add-replacement-modal.success', {
          count: itemsToAdd.length,
        }),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'added_replacement_items',
        object: 'shopify_order',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        items_count: itemsToAdd.length,
        notify_customer: this.notifyCustomer,
      });

      this.handleModalClose();
      this.args.onReplacementSuccess?.();
    } catch (error) {
      this.notificationsService.notifyError(
        (error as Error).message ||
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.add-replacement-modal.errors.generic',
          ),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::AddReplacementItemModal': typeof AddReplacementItemModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/add-replacement-item-modal': typeof AddReplacementItemModal;
  }
}
